// ReportsPanel.js — Report management: sent/failed reports, recipients, send/preview actions.
// Table of reports, editable recipients, Send Now and Preview PDF buttons.

import React, { useState, useCallback } from 'react';
import { FileText, Send, Eye, Users, CheckCircle, XCircle, Clock, Edit2, X, Plus } from 'lucide-react';

const STATUS_CONFIG = {
  sent: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Sent' },
  failed: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', label: 'Failed' },
  pending: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Pending' },
  queued: { icon: Clock, color: 'text-zinc-500', bg: 'bg-zinc-100', label: 'Queued' },
};

function fmtDate(dateStr) {
  if (!dateStr) return '--';
  const d = new Date(dateStr);
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.1em] ${config.color} ${config.bg}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

export default function ReportsPanel({ reports = [], recipients = [], onSendNow, onPreview, onUpdateRecipients }) {
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [editingRecipients, setEditingRecipients] = useState(false);
  const [recipientDraft, setRecipientDraft] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [saveStatus, setSaveStatus] = useState(null); // 'saving' | 'saved' | 'error'
  const [saveError, setSaveError] = useState(null);

  const handleSendNow = useCallback(async () => {
    if (!onSendNow) return;
    setSending(true);
    try {
      await onSendNow();
    } finally {
      setSending(false);
    }
  }, [onSendNow]);

  const handlePreview = useCallback(async () => {
    if (!onPreview) return;
    setPreviewing(true);
    try {
      await onPreview();
    } finally {
      setPreviewing(false);
    }
  }, [onPreview]);

  const handleEditRecipients = useCallback(() => {
    setRecipientDraft([...recipients]);
    setEditingRecipients(true);
  }, [recipients]);

  const handleRemoveRecipient = useCallback((index) => {
    setRecipientDraft(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleAddRecipient = useCallback(() => {
    const email = newEmail.trim();
    if (!email) return;
    // Basic email check
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    if (recipientDraft.includes(email)) return;
    setRecipientDraft(prev => [...prev, email]);
    setNewEmail('');
  }, [newEmail, recipientDraft]);

  const handleUpdateDraft = useCallback((index, value) => {
    setRecipientDraft(prev => prev.map((email, i) => i === index ? value : email));
  }, []);

  const handleSaveRecipients = useCallback(async () => {
    if (!onUpdateRecipients) return;
    setSaveStatus('saving');
    setSaveError(null);
    try {
      // Include any email typed in the add-field that wasn't explicitly added via +
      let all = [...recipientDraft];
      const pending = newEmail.trim();
      if (pending && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pending) && !all.includes(pending)) {
        all.push(pending);
      }
      const cleaned = all.map(e => e.trim()).filter(e => e.length > 0);
      await onUpdateRecipients(cleaned);
      setSaveStatus('saved');
      setEditingRecipients(false);
      setNewEmail('');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      setSaveStatus('error');
      setSaveError(err.message || 'Failed to save');
    }
  }, [recipientDraft, newEmail, onUpdateRecipients]);

  const handleCancelEdit = useCallback(() => {
    setEditingRecipients(false);
    setRecipientDraft([]);
    setNewEmail('');
  }, []);

  return (
    <div className="bg-white border border-zinc-200 rounded-none p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4" style={{ color: '#4D4D4D' }} />
          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
            Reports
          </span>
        </div>
        <span className="font-mono text-[10px] text-zinc-400">
          {reports.length} report{reports.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={handleSendNow}
          disabled={sending}
          className="flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: sending ? '#a1a1aa' : '#4D4D4D' }}
        >
          <Send className="w-3.5 h-3.5" />
          {sending ? 'Sending...' : 'Send Now'}
        </button>
        <button
          onClick={handlePreview}
          disabled={previewing}
          className="flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] border border-zinc-200 hover:bg-zinc-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ color: '#4D4D4D' }}
        >
          <Eye className="w-3.5 h-3.5" />
          {previewing ? 'Loading...' : 'Preview PDF'}
        </button>
      </div>

      {/* Reports table */}
      <div className="mb-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">
          Report History
        </p>
        {reports.length === 0 ? (
          <div className="text-center py-6">
            <p className="font-mono text-xs text-zinc-400">
              No reports generated yet.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-left py-2 px-2 font-medium">
                    Date
                  </th>
                  <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-left py-2 px-2 font-medium">
                    Type
                  </th>
                  <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-left py-2 px-2 font-medium">
                    Status
                  </th>
                  <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-left py-2 px-2 font-medium">
                    Recipients
                  </th>
                  <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-left py-2 px-2 font-medium">
                    Error
                  </th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report, i) => (
                  <tr key={report.id || i} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                    <td className="py-1.5 px-2 font-mono tabular-nums text-zinc-700 whitespace-nowrap">
                      {fmtDate(report.sent_at || report.created_at)}
                    </td>
                    <td className="py-1.5 px-2 font-mono text-zinc-700">
                      {report.report_type || 'Weekly'}
                    </td>
                    <td className="py-1.5 px-2">
                      <StatusBadge status={report.status} />
                    </td>
                    <td className="py-1.5 px-2 font-mono text-zinc-500 text-xs max-w-xs truncate">
                      {report.recipient_count != null
                        ? `${report.recipient_count} recipient${report.recipient_count !== 1 ? 's' : ''}`
                        : '--'}
                    </td>
                    <td className="py-1.5 px-2 font-mono text-red-500 text-xs max-w-xs truncate">
                      {report.error_message || ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recipients section */}
      <div className="border-t border-zinc-200 pt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Users className="w-3.5 h-3.5" style={{ color: '#4D4D4D' }} />
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
              Recipients
            </span>
          </div>
          <div className="flex items-center gap-2">
          {saveStatus === 'saved' && (
            <span className="font-mono text-[10px] text-emerald-600">Saved</span>
          )}
          {!editingRecipients && (
            <button
              onClick={handleEditRecipients}
              className="flex items-center gap-1 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] border border-zinc-200 hover:bg-zinc-50 transition-colors"
              style={{ color: '#4D4D4D' }}
            >
              <Edit2 className="w-3 h-3" />
              Edit
            </button>
          )}
        </div>
        </div>

        {!editingRecipients ? (
          /* Read-only list */
          <div className="space-y-1">
            {recipients.length === 0 ? (
              <p className="font-mono text-xs text-zinc-400">
                No recipients configured.
              </p>
            ) : (
              recipients.map((email, i) => (
                <div key={i} className="flex items-center gap-2 py-1">
                  <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full flex-shrink-0" />
                  <span className="font-mono text-xs text-zinc-700">{email}</span>
                </div>
              ))
            )}
          </div>
        ) : (
          /* Editable list */
          <div className="space-y-2">
            {recipientDraft.map((email, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={e => handleUpdateDraft(i, e.target.value)}
                  className="flex-1 px-2 py-1.5 font-mono text-xs border border-zinc-200 bg-white text-zinc-900 focus:outline-none focus:border-zinc-400 transition-colors"
                />
                <button
                  onClick={() => handleRemoveRecipient(i)}
                  className="p-0.5 text-zinc-400 hover:text-red-500 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}

            {/* Add new recipient */}
            <div className="flex items-center gap-2">
              <input
                type="email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddRecipient()}
                placeholder="email@example.com"
                className="flex-1 px-2 py-1.5 font-mono text-xs border border-zinc-200 bg-white text-zinc-900 focus:outline-none focus:border-zinc-400 transition-colors"
              />
              <button
                onClick={handleAddRecipient}
                className="p-1.5 border border-zinc-200 hover:bg-zinc-50 transition-colors"
                style={{ color: '#4D4D4D' }}
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Save / Cancel */}
            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={handleSaveRecipients}
                disabled={saveStatus === 'saving'}
                className="flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#4D4D4D' }}
              >
                {saveStatus === 'saving' ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancelEdit}
                className="flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 border border-zinc-200 hover:bg-zinc-50 transition-colors"
              >
                Cancel
              </button>
              {saveStatus === 'error' && (
                <span className="font-mono text-[10px] text-red-500">{saveError}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
